import React from 'react';

import BraintreePaymentMethod from '../BraintreePaymentMethod/BraintreePaymentMethod';

import Overlays from '@components/Overlays';
import SentryErrorBoundary from '@components/SentryErrorBoundary/SentryErrorBoundary';
import ConfigProvider from '@providers/ConfigProvider';
import CreditCardsMutationProvider from '@providers/CreditCardsMutationProvider';
import ErrorContextProvider from '@providers/ErrorContextProvider';
import I18nProvider from '@providers/I18nProvider';
import Providers from '@providers/Providers';
import {
  ContainerType,
  CreditCardData,
  CustomerType,
  Environment,
  Locale,
  PaymentMethod,
  ShopBundle,
} from '@types-internal';

export interface BasePaymentMethodSwitchProps {
  errorContainerId: string;
  containerType: ContainerType;
  customerType: CustomerType;
  shopBundle: ShopBundle;
  domain: string;
  locale: Locale;
  csrfToken?: string; // csrfToken might be undefined for a form not needing API calls (for ex on registration page)
  isFallbackPsp: boolean;
  env: Environment;
  currency?: string;
  customerId?: string;
  sid?: string;
  siteId?: number;
}

export interface PaymentMethodSwitchProps extends BasePaymentMethodSwitchProps {
  paymentMethod: PaymentMethod;
  pspToken: string;
  creditCards: CreditCardData[];
  env: Environment;
}

/**
 * UI to display the Braintree payment method.
 */
const PaymentMethodSwitch: React.FC<PaymentMethodSwitchProps> = (props) => {
  const {
    errorContainerId,
    containerType,
    customerType,
    shopBundle,
    domain,
    locale,
    csrfToken,
    paymentMethod,
    isFallbackPsp,
    env,
    currency,
    customerId,
    sid,
    siteId,
  } = props;

  return (
    <ConfigProvider env={env} shopBundle={shopBundle}>
      <I18nProvider env={env} shopBundle={shopBundle} locale={locale}>
        <SentryErrorBoundary featureName="braintree-credit-card">
          <ErrorContextProvider errorContainerId={errorContainerId}>
            <Providers
              shopBundle={shopBundle}
              domain={domain}
              locale={locale}
              csrfToken={csrfToken}>
              {paymentMethod === 'BK' && (
                <CreditCardsMutationProvider
                  pspToken={props.pspToken}
                  creditCards={props.creditCards}
                  paymentMethod="CREDIT_CARD"
                  psp="BRAINTREE">
                  <BraintreePaymentMethod
                    containerType={containerType}
                    customerType={customerType}
                    token={props.pspToken}
                    isFallbackPsp={isFallbackPsp}
                    currency={currency}
                    customerId={customerId}
                    sid={sid}
                    siteId={siteId}
                  />
                  <Overlays
                    containerType={containerType}
                    currency={currency}
                    customerId={customerId}
                    sid={sid}
                    siteId={siteId}
                  />
                </CreditCardsMutationProvider>
              )}
            </Providers>
          </ErrorContextProvider>
        </SentryErrorBoundary>
      </I18nProvider>
    </ConfigProvider>
  );
};

export default PaymentMethodSwitch;
